import { useState } from 'react';
import {
  Card, CardContent, CardHeader, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Tooltip, Grid,
} from '@mui/material';
import { Add, PlayArrow, Pause, NetworkCheck, Delete, Replay, History, ContentCopy } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { webhookApi, type WebhookEndpoint, type WebhookDelivery } from '../../api/integration.api';

const deliveryColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  success: 'success', pending: 'info', retrying: 'warning', failed: 'error', dead: 'error',
};

export function WebhooksPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [incomingOpen, setIncomingOpen] = useState(false);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookEndpoint | null>(null);

  const endpoints = useQuery({ queryKey: ['webhook-endpoints'], queryFn: () => webhookApi.listEndpoints({ limit: 100 }) });
  const incoming = useQuery({ queryKey: ['incoming-webhooks'], queryFn: () => webhookApi.listIncoming({ limit: 100 }) });

  const refetchOut = () => qc.invalidateQueries({ queryKey: ['webhook-endpoints'] });
  const refetchIn = () => qc.invalidateQueries({ queryKey: ['incoming-webhooks'] });
  const setStatus = useMutation({ mutationFn: ({ id, action }: { id: number; action: 'activate' | 'pause' }) => webhookApi.setStatus(id, action), onSuccess: refetchOut });
  const removeEp = useMutation({ mutationFn: (id: number) => webhookApi.remove(id), onSuccess: refetchOut });
  const test = useMutation({ mutationFn: (id: number) => webhookApi.test(id), onSuccess: refetchOut });
  const removeIn = useMutation({ mutationFn: (id: number) => webhookApi.removeIncoming(id), onSuccess: refetchIn });

  return (
    <>
      <PageHeader
        title="Webhooks"
        subtitle="Outgoing event subscriptions (HMAC-signed, retried) and inbound receivers."
        action={
          <Stack direction="row" spacing={1}>
            {hasPermission('webhook.create') && <Button startIcon={<Add />} variant="outlined" onClick={() => setIncomingOpen(true)}>Incoming</Button>}
            {hasPermission('webhook.create') && <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>Outgoing</Button>}
          </Stack>
        }
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Outgoing endpoints" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(endpoints.data?.data.length ?? 0) === 0 ? <EmptyState title="No outgoing webhooks" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Target</TableCell><TableCell>Events</TableCell><TableCell>Failures</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                  <TableBody>
                    {endpoints.data?.data.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell>{e.name}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.target_url}</TableCell>
                        <TableCell>{e.event_types?.length ? e.event_types.join(', ') : 'all'}</TableCell>
                        <TableCell>{e.failure_count}</TableCell>
                        <TableCell><Chip size="small" color={e.status === 'active' ? 'success' : 'default'} label={e.status} /></TableCell>
                        <TableCell align="right">
                          <Tooltip title="Deliveries"><IconButton size="small" onClick={() => setDeliveriesFor(e)}><History fontSize="small" /></IconButton></Tooltip>
                          {hasPermission('webhook.create') && <Tooltip title="Test"><IconButton size="small" onClick={() => test.mutate(e.id)}><NetworkCheck fontSize="small" /></IconButton></Tooltip>}
                          {hasPermission('webhook.update') && (e.status === 'active'
                            ? <Tooltip title="Pause"><IconButton size="small" onClick={() => setStatus.mutate({ id: e.id, action: 'pause' })}><Pause fontSize="small" /></IconButton></Tooltip>
                            : <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => setStatus.mutate({ id: e.id, action: 'activate' })}><PlayArrow fontSize="small" /></IconButton></Tooltip>)}
                          {hasPermission('webhook.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => removeEp.mutate(e.id)}><Delete fontSize="small" /></IconButton></Tooltip>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Incoming receivers" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(incoming.data?.data.length ?? 0) === 0 ? <EmptyState title="No incoming receivers" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Endpoint</TableCell><TableCell>Source</TableCell><TableCell>Handler</TableCell><TableCell>Received</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                  <TableBody>
                    {incoming.data?.data.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.name}</TableCell>
                        <TableCell><code>/gateway/incoming/{r.slug}</code></TableCell>
                        <TableCell>{r.source ?? '—'}</TableCell>
                        <TableCell>{r.handler_key}</TableCell>
                        <TableCell>{r.received_count}</TableCell>
                        <TableCell align="right">{hasPermission('webhook.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => removeIn.mutate(r.id)}><Delete fontSize="small" /></IconButton></Tooltip>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {createOpen && <CreateEndpointDialog onClose={() => setCreateOpen(false)} onCreated={(s) => { setCreateOpen(false); setSecret({ title: 'Signing secret', value: s }); refetchOut(); }} />}
      {incomingOpen && <CreateIncomingDialog onClose={() => setIncomingOpen(false)} onCreated={(s) => { setIncomingOpen(false); setSecret({ title: 'HMAC secret', value: s }); refetchIn(); }} />}
      {deliveriesFor && <DeliveriesDialog endpoint={deliveriesFor} onClose={() => setDeliveriesFor(null)} />}

      <Dialog open={Boolean(secret)} onClose={() => setSecret(null)} fullWidth maxWidth="sm">
        <DialogTitle>{secret?.title} created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Copy this secret now — it will not be shown again.</Alert>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField fullWidth value={secret?.value ?? ''} InputProps={{ readOnly: true }} />
            <IconButton onClick={() => secret && navigator.clipboard?.writeText(secret.value)}><ContentCopy /></IconButton>
          </Stack>
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setSecret(null)}>Done</Button></DialogActions>
      </Dialog>
    </>
  );
}

function CreateEndpointDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (secret: string) => void }) {
  const [form, setForm] = useState({ name: '', targetUrl: '', eventTypes: '', maxRetries: '5' });
  const create = useMutation({
    mutationFn: () => webhookApi.createEndpoint({ name: form.name, targetUrl: form.targetUrl, eventTypes: form.eventTypes.split(',').map((s) => s.trim()).filter(Boolean), maxRetries: Number(form.maxRetries) || 5 }),
    onSuccess: (r) => onCreated(r.signingSecret),
  });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Outgoing Webhook</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField label="Target URL" placeholder="https://partner.example.com/hooks" value={form.targetUrl} onChange={(e) => setForm((f) => ({ ...f, targetUrl: e.target.value }))} />
          <TextField label="Event types (comma-separated, blank = all)" placeholder="order.created, order.shipped" value={form.eventTypes} onChange={(e) => setForm((f) => ({ ...f, eventTypes: e.target.value }))} />
          <TextField label="Max retries" type="number" value={form.maxRetries} onChange={(e) => setForm((f) => ({ ...f, maxRetries: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={!form.name || !form.targetUrl || create.isPending} onClick={() => create.mutate()}>Create</Button></DialogActions>
    </Dialog>
  );
}

function CreateIncomingDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (secret: string) => void }) {
  const [form, setForm] = useState({ name: '', slug: '', source: '' });
  const create = useMutation({ mutationFn: () => webhookApi.createIncoming({ name: form.name, slug: form.slug, source: form.source || undefined }), onSuccess: (r) => onCreated(r.hmacSecret) });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Incoming Receiver</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField label="Slug (lowercase, dashes)" placeholder="client-oms" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
          <TextField label="Source (optional)" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={!form.name || !form.slug || create.isPending} onClick={() => create.mutate()}>Create</Button></DialogActions>
    </Dialog>
  );
}

function DeliveriesDialog({ endpoint, onClose }: { endpoint: WebhookEndpoint; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const { data } = useQuery({ queryKey: ['webhook-deliveries', endpoint.id], queryFn: () => webhookApi.deliveries(endpoint.id) });
  const replay = useMutation({ mutationFn: (id: number) => webhookApi.replay(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook-deliveries', endpoint.id] }) });
  const rows: WebhookDelivery[] = data ?? [];
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>Deliveries — {endpoint.name}</DialogTitle>
      <DialogContent>
        {rows.length === 0 ? <EmptyState title="No deliveries yet" dense /> : (
          <Table size="small">
            <TableHead><TableRow><TableCell>When</TableCell><TableCell>Event</TableCell><TableCell>Attempts</TableCell><TableCell>HTTP</TableCell><TableCell>Status</TableCell><TableCell>Error</TableCell><TableCell align="right" /></TableRow></TableHead>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>{new Date(d.created_at).toLocaleString()}</TableCell>
                  <TableCell>{d.event_type}</TableCell>
                  <TableCell>{d.attempt_count}/{d.max_attempts}</TableCell>
                  <TableCell>{d.response_status ?? '—'}</TableCell>
                  <TableCell><Chip size="small" color={deliveryColor[d.status]} label={d.status} /></TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.last_error ?? '—'}</TableCell>
                  <TableCell align="right">{hasPermission('webhook.replay') && <Tooltip title="Replay"><IconButton size="small" onClick={() => replay.mutate(d.id)}><Replay fontSize="small" /></IconButton></Tooltip>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
